import { useEffect, useRef } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { LoomOnboardingText } from './loom-onboarding-text';

import type { Recorder } from '../utilities/types';

const popupWrapperCss = css({
	position: 'absolute',
	insetBlockStart: '100%',
	insetInlineStart: 0,
	insetInlineEnd: 0,
	marginBlockStart: token('space.100'),
	zIndex: 1100,
});

const popupCss = css({
	display: 'flex',
	flexDirection: 'column',
	backgroundColor: token('elevation.surface.overlay'),
	borderColor: token('color.border'),
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	borderRadius: '8px',
	boxShadow: token('elevation.shadow.overlay'),
	overflow: 'hidden',
});

const listCss = css({
	listStyle: 'none',
	margin: 0,
	padding: token('space.150'),
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.050'),
});

const optionCss = css({
	display: 'flex',
	alignItems: 'center',
	minHeight: '40px',
	paddingBlock: token('space.100'),
	paddingInline: token('space.150'),
	borderRadius: '6px',
	cursor: 'pointer',
	backgroundColor: 'transparent',
	border: 'none',
	textAlign: 'left',
	width: '100%',
	'&:hover, &:focus-visible': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		outline: 'none',
	},
});

const helpOptionCss = css({
	color: token('color.link'),
});

export const DownloadMacOptionsDropdown = ({
	handleClick,
	onNeedHelp,
	onClose,
}: {
	handleClick: (recorder: Recorder) => void;
	onNeedHelp: () => void;
	onClose: () => void;
}) => {
	const wrapperRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handlePointerDown = (event: MouseEvent | TouchEvent) => {
			const target = event.target as Node | null;
			if (wrapperRef.current && target && !wrapperRef.current.contains(target)) {
				onClose();
			}
		};
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose();
			}
		};

		document.addEventListener('mousedown', handlePointerDown);
		document.addEventListener('touchstart', handlePointerDown);
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('mousedown', handlePointerDown);
			document.removeEventListener('touchstart', handlePointerDown);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [onClose]);

	return (
		<div ref={wrapperRef} css={popupWrapperCss} data-test-id="download-mac-options-dropdown">
			<div css={popupCss}>
				<ul css={listCss} aria-label="Choose your Mac chip">
					<li>
						<button type="button" css={optionCss} onClick={() => handleClick('intel')}>
							<LoomOnboardingText inline>Download for MacOS - Intel chip</LoomOnboardingText>
						</button>
					</li>
					<li>
						<button type="button" css={optionCss} onClick={() => handleClick('apple')}>
							<LoomOnboardingText inline>Download for MacOS - Apple M chip</LoomOnboardingText>
						</button>
					</li>
					<li>
						<button type="button" css={[optionCss, helpOptionCss]} onClick={onNeedHelp}>
							<LoomOnboardingText inline>
								<span css={helpOptionCss}>Need help choosing?</span>
							</LoomOnboardingText>
						</button>
					</li>
				</ul>
			</div>
		</div>
	);
};
